import { DEFAULT_ICON_FILE } from "./icon";
import type { CustomIndicatorProjectKind } from "./types";

export const DEFAULT_CSPROJ_FILE = "Indicator.csproj";
export const DEFAULT_CS_ENTRY_FILE = "Indicator.cs";

export interface CsprojMetadata {
  name: string;
  version: string;
  description: string;
  overlay: boolean;
  entryFile: string;
  iconFile: string;
  kind: CustomIndicatorProjectKind;
  assemblyName?: string;
}

const DEFAULT_META: CsprojMetadata = {
  name: "My Custom Indicator",
  version: "1.0",
  description: "",
  overlay: true,
  entryFile: DEFAULT_CS_ENTRY_FILE,
  iconFile: DEFAULT_ICON_FILE,
  kind: "indicator",
  assemblyName: "Indicator",
};

export function defaultCsprojXml(meta: Partial<CsprojMetadata> = {}): string {
  const merged = { ...DEFAULT_META, ...meta };
  const m: CsprojMetadata = {
    name: merged.name ?? DEFAULT_META.name,
    version: merged.version ?? DEFAULT_META.version,
    description: merged.description ?? DEFAULT_META.description,
    overlay: merged.overlay ?? DEFAULT_META.overlay,
    entryFile: merged.entryFile ?? DEFAULT_META.entryFile,
    iconFile: merged.iconFile ?? DEFAULT_META.iconFile,
    kind: merged.kind ?? DEFAULT_META.kind,
    assemblyName: merged.assemblyName ?? DEFAULT_META.assemblyName,
  };
  const kind =
    m.kind === "trading-algo" ? "TradingAlgo" : "Indicator";
  return `<Project Sdk="Microsoft.NET.Sdk">
  <PropertyGroup>
    <TargetFramework>net10.0</TargetFramework>
    <Nullable>enable</Nullable>
    <ImplicitUsings>enable</ImplicitUsings>
    <RootNamespace>Vybe.UserIndicators</RootNamespace>
    <AssemblyName>${escapeXml(m.assemblyName ?? "Indicator")}</AssemblyName>
    <Version>${escapeXml(m.version)}</Version>
    <Description>${escapeXml(m.description)}</Description>
    <VybeIndicatorName>${escapeXml(m.name)}</VybeIndicatorName>
    <VybeProjectKind>${kind}</VybeProjectKind>
    <VybeEntryFile>${escapeXml(m.entryFile)}</VybeEntryFile>
    <VybeIconFile>${escapeXml(m.iconFile)}</VybeIconFile>
    <VybeOverlay>${m.overlay ? "true" : "false"}</VybeOverlay>
  </PropertyGroup>
  <ItemGroup>
    <Compile Include="${escapeXml(m.entryFile)}" />
  </ItemGroup>
</Project>
`;
}

export function parseCsproj(xml: string): CsprojMetadata {
  const meta = { ...DEFAULT_META };
  if (!xml?.trim()) return meta;

  const read = (tag: string) => {
    const re = new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`, "i");
    const m = xml.match(re);
    return m?.[1]?.trim() ?? "";
  };

  const name = read("VybeIndicatorName");
  if (name) meta.name = name;
  const asm = read("AssemblyName");
  if (asm) meta.assemblyName = asm;
  const ver = read("Version");
  if (ver) meta.version = ver;
  const desc = read("Description");
  if (desc) meta.description = desc;
  const entry = read("VybeEntryFile");
  if (entry) meta.entryFile = entry;
  const iconFile = read("VybeIconFile");
  if (iconFile) meta.iconFile = iconFile;
  const overlay = read("VybeOverlay");
  if (overlay) meta.overlay = overlay.toLowerCase() === "true";
  const kind = read("VybeProjectKind");
  if (kind.toLowerCase() === "tradingalgo" || kind.toLowerCase() === "trading-algo") {
    meta.kind = "trading-algo";
  } else {
    meta.kind = "indicator";
  }

  return meta;
}

export function updateCsprojXml(
  currentXml: string,
  patch: Partial<CsprojMetadata>
): string {
  const merged = { ...parseCsproj(currentXml), ...patch };
  return defaultCsprojXml(merged);
}

function escapeXml(value: string | undefined | null): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
